import { getGratificationById } from 'api/v1/gratifications';
import {
  getConversationMessages,
  getConversationMetadata,
  getConversationUsage,
  postConversationMetadata,
} from 'api/v2/AI';
import { uploadFile } from 'api/v2/upload';
import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useCustomDropzone from 'hooks/useCustomDropzone';
import { get } from 'lodash';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useQuery from 'hooks/react-query/useQuery';
import { keepPreviousData, type UseQueryResult } from '@tanstack/react-query';
import type { ConversationMetadata } from 'types/ai.types';
import {
  AIRoleEnum,
  type AIUsage,
  type ConversationHistory,
} from 'types/ai.types';
import { AICacheKeys } from 'types/cacheKeys.types';
import { UploadTypesEnum } from 'types/uploadEnums.types';
import { globalyAcceptedFileUploaderTypes, maxFileSize } from 'utils/constants';
import { formatMetadata } from 'utils/gratifications/getMetaData';
import { gratificationsData } from 'utils/gratifications/gratificationsData';
import type { AIChatAttachment, AIChatAttachmentApi } from './AIChat.types';
import { getStoredAuthData } from 'utils/auth';
import { FileUploadStatus } from 'hooks/filesUploadStoreFactory';

const MAX_ATTACHMENTS = 5;

export const useGetGratificationById = (currentTipId: string) => {
  const { t } = useTranslation();
  return useQuery(
    [currentTipId],
    () => getGratificationById(currentTipId as string),
    {
      enabled: !!currentTipId,
      select: (tip) => {
        const _meta = formatMetadata(get(tip, 'metadata'));
        const gratifContent = gratificationsData[tip.gratificationNumber];
        const tipTextContent = get(gratifContent, 'helperContent', [])?.find(
          (comp) => !!comp.text,
        );
        const description = tipTextContent
          ? t(tipTextContent?.text as string, _meta)
          : '';
        const title = t(
          get(gratificationsData, [tip.gratificationNumber, 'text']),
          _meta,
        );
        return {
          ...tip,
          title,
          description,
          gratifContent: gratificationsData[tip.gratificationNumber],
        };
      },
    },
  );
};

export const useConversationQuery = (
  conversationId: string,
  onSuccess: (data: ConversationHistory) => void,
): UseQueryResult<ConversationHistory> =>
  useQuery(
    [AICacheKeys.ConversationMessages, conversationId],
    () => getConversationMessages(conversationId),
    {
      onSuccess,
      placeholderData: keepPreviousData,
      enabled: !!conversationId,
      gcTime: 0,
    },
  );

export const useUsageQuery = (
  conversation: ConversationHistory | null,
  message: string,
) => {
  const { data: user } = useGetCurrentCustomerData();

  const isAccountableUser = user?.email.endsWith('@accountable.eu');
  const currentCount =
    conversation?.messages?.filter((m) => m.role === AIRoleEnum.user).length ||
    0;

  const defaultUsage: AIUsage = {
    reachedMaxCount: false,
    messageLengthExceeded: message?.length > 1500,
    currentCount,
    reachedMaxMonthlyCount: false,
    monthly_message_count: 0,
    monthly_message_limit: 50,
    conversation_message_limit: 5,
  };

  const usageQuery = useQuery(
    [AICacheKeys.ConversationUsage],
    getConversationUsage,
    {
      enabled: !!getStoredAuthData('access_token'),
      placeholderData: keepPreviousData,
      select: ({
        monthly_message_count = 0,
        monthly_message_limit = 50,
        conversation_message_limit = 5,
      }) => ({
        ...defaultUsage,
        reachedMaxCount: currentCount >= conversation_message_limit,
        reachedMaxMonthlyCount:
          !isAccountableUser && monthly_message_count >= monthly_message_limit,
        monthly_message_count,
        monthly_message_limit,
        conversation_message_limit,
      }),
    },
  );

  return { ...usageQuery, data: usageQuery.data || defaultUsage };
};

export const useConversationMetadata = (
  conversationId?: string,
  tempMetadata?: ConversationMetadata | null,
) => {
  const {
    data: { data: metadataItems = [] } = {},
    isLoading,
    refetch,
  } = useQuery(
    [AICacheKeys.ConversationMetadata, conversationId],
    () => getConversationMetadata(conversationId!),
    { enabled: !!conversationId?.length },
  );

  // concatenate tempMetadata if it provided
  if (tempMetadata) metadataItems.push(tempMetadata);

  return { metadataItems, isLoading, refetchMetadata: refetch };
};

export const useAIChatAttachment = (
  allowFileMetadata = false,
): AIChatAttachmentApi => {
  const [attachments, setAttachments] = useState<AIChatAttachment[]>([]);
  const uploadIdCounter = useRef(0);
  const uploadCancelTokens = useRef<Record<number, CancelTokenSource | null>>(
    {},
  );
  const attachmentMetadataIds = useRef<(string | undefined)[]>([]);

  const reachedAttachmentsLimit = attachments.length >= MAX_ATTACHMENTS;

  const uploadAttachment = async (file: File) => {
    const isPdf = file.type === 'application/pdf';
    const uploadId = uploadIdCounter.current++;

    const newAttachment: AIChatAttachment = {
      _uploadId: uploadId,
      status: FileUploadStatus.uploading,
      type: isPdf ? 'pdf' : 'image',
      file,
      progress: 0,
    };

    uploadCancelTokens.current[uploadId] = axios.CancelToken.source();
    setAttachments((prev) => [...prev, newAttachment]);

    const onUploadProgress = (progress: number) => {
      setAttachments((prev) =>
        prev.map((a) =>
          a._uploadId === uploadId
            ? { ...a, progress: Math.round(progress) }
            : a,
        ),
      );
    };

    try {
      const { key } = await uploadFile({
        file,
        category: UploadTypesEnum.conversationAttachment,
        onProgress: onUploadProgress,
        cancelToken: uploadCancelTokens.current[uploadId]!.token,
      });
      if (!uploadCancelTokens.current[uploadId]) return;

      const metadata: ConversationMetadata = {
        documentType: newAttachment.type,
        data: {
          filePath: key,
          filename: key.split('/').pop(),
          originalFilename: file.name,
        },
      };

      const { data: saved_metadata } = await postConversationMetadata(metadata);

      if (!uploadCancelTokens.current[uploadId]) return;

      attachmentMetadataIds.current[uploadId] = saved_metadata._id;
      setAttachments((prev) =>
        prev.map((a) =>
          a._uploadId === uploadId
            ? { ...a, status: FileUploadStatus.done }
            : a,
        ),
      );
    } catch (error) {
      if (!axios.isCancel(error)) {
        setAttachments((prev) =>
          prev.map((a) =>
            a._uploadId === uploadId
              ? { ...a, status: FileUploadStatus.error }
              : a,
          ),
        );
      }
    }
  };

  const handleFileSelect = useCallback(
    async (files: File[]) => {
      if (!files?.length || !allowFileMetadata) return;
      const slotsAvailable = MAX_ATTACHMENTS - attachments.length;
      const filesToUpload = files.slice(0, slotsAvailable);
      await Promise.all(filesToUpload.map((file) => uploadAttachment(file)));
    },
    [allowFileMetadata, attachments.length],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useCustomDropzone(
    {
      onDropAccepted: handleFileSelect,
      accept: globalyAcceptedFileUploaderTypes,
      maxSize: maxFileSize,
      noClick: true,
      disabled: !allowFileMetadata || reachedAttachmentsLimit,
    },
  );

  const remove = (index: number) => {
    const uploadId = attachments[index]?._uploadId;
    if (uploadId === undefined) {
      setAttachments((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    uploadCancelTokens.current[uploadId]?.cancel();
    uploadCancelTokens.current[uploadId] = null;
    delete attachmentMetadataIds.current[uploadId];
    setAttachments((prev) => prev.filter((a) => a._uploadId !== uploadId));
  };

  const removeAll = () => {
    Object.values(uploadCancelTokens.current).forEach((token) =>
      token?.cancel(),
    );
    uploadCancelTokens.current = {};
    attachmentMetadataIds.current = [];
    setAttachments([]);
  };

  return {
    attachments,
    attachmentMetadataIds,
    reachedAttachmentsLimit,
    enabled: allowFileMetadata,
    upload: uploadAttachment,
    open,
    remove,
    removeAll,
    dropzoneProps: {
      root: getRootProps,
      input: getInputProps,
      isDragActive,
    },
  };
};
